import { memo, useState, useCallback, useRef } from "react";
import { Button, Space, Typography } from "antd";
import PropTypes from "prop-types";

import "./UnstractSubscription.css";
import { useSessionStore } from "../../../store/session-store";
import { useAlertStore } from "../../../store/alert-store";
import { useExceptionHandler } from "../../../hooks/useExceptionHandler";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import useRequestUrl from "../../../hooks/useRequestUrl";

const SubscriptionPlanCard = memo(
  ({
    plan,
    isAnnual,
    stripePromise,
    isAlreadySubscribed,
    isSubscriptionCancelled,
  }) => {
    const [isLoading, setIsLoading] = useState(false);
    const isCancelled = useRef(false);

    const { sessionDetails } = useSessionStore();
    const { setAlertDetails } = useAlertStore();
    const handleException = useExceptionHandler();
    const axiosPrivate = useAxiosPrivate();
    const { getUrl } = useRequestUrl();

    const {
      id,
      planName,
      image,
      pricePerYear,
      pricePerMonth,
      overagePerMonth,
      overagePerYear,
      numberOfPagesPerMonth,
      numberOfPagesPerYear,
    } = plan || {};

    const handleSubscribe = useCallback(() => {
      if (isAlreadySubscribed && !isSubscriptionCancelled) {
        setAlertDetails({
          type: "error",
          content: "Subscription already exists",
        });
        return;
      }

      if (!id) {
        setAlertDetails({
          type: "error",
          content: "Product ID is not available.",
        });
        return;
      }

      setIsLoading(true);
      isCancelled.current = false;

      // Handler to cancel the subscription if user clicks back.
      const handlePopState = () => {
        isCancelled.current = true;
        setIsLoading(false);
      };
      window.addEventListener("popstate", handlePopState);

      // Set a timeout of 30 seconds. If stripe redirect hasn't happened, cancel.
      const timer = setTimeout(() => {
        if (!isCancelled.current) {
          isCancelled.current = true;
          setAlertDetails(handleException("Stripe redirect timed out"));
          setIsLoading(false);
        }
      }, 30000);

      stripePromise
        .then((stripe) => {
          if (isCancelled.current) {
            return Promise.reject(new Error("Cancelled"));
          }
          let planId = id;
          if (isAnnual) {
            planId += "-annual";
          }

          const requestOptions = {
            method: "POST",
            url: getUrl("/create-checkout-session/"),
            headers: {
              "X-CSRFToken": sessionDetails?.csrfToken,
            },
            data: {
              id: planId,
            },
          };

          return axiosPrivate(requestOptions).then((response) => {
            if (isCancelled.current) {
              return Promise.reject(new Error("Cancelled"));
            }
            return { stripe, sessionId: response?.data?.session_id };
          });
        })
        .then(({ stripe, sessionId }) => {
          if (isCancelled.current) {
            return Promise.reject(new Error("Cancelled"));
          }
          return stripe.redirectToCheckout({ sessionId }).then((result) => {
            if (isCancelled.current) {
              return;
            }
            if (result.error) {
              setAlertDetails(
                handleException(result.error, "Subscription not found")
              );
            }
          });
        })
        .catch((err) => {
          if (err.message !== "Cancelled") {
            setAlertDetails(handleException(err, "Failed to subscribe"));
          }
        })
        .finally(() => {
          setIsLoading(false);
          clearTimeout(timer);
          window.removeEventListener("popstate", handlePopState);
        });
    }, [plan, isAnnual, isAlreadySubscribed, isSubscriptionCancelled]);

    return (
      <div className="subscription-plan-card">
        <div className="display-flex-center-h-and-v subscription-plan-card-section image">
          {image}
        </div>
        <div className="display-flex-center-h-and-v subscription-plan-card-section plan-name">
          <Typography.Text strong>{planName}</Typography.Text>
        </div>
        <div className="display-flex-center-h-and-v subscription-plan-card-section price">
          <Space direction="vertical" className="display-flex-align-center">
            <Space className="subscription-price-row">
              <Typography.Text className="subscription-price-text" strong>
                ${isAnnual ? pricePerYear : pricePerMonth}
              </Typography.Text>
              <div className="subscription-month-wrapper">
                <Typography.Text className="subscription-month-text">
                  /month
                </Typography.Text>
              </div>
            </Space>
            <Typography.Text type="secondary">
              billed {isAnnual ? "annually" : "monthly"}
            </Typography.Text>
            <Button
              type="primary"
              onClick={handleSubscribe}
              loading={isLoading}
              disabled={isAlreadySubscribed && !isSubscriptionCancelled}
            >
              Subscribe
            </Button>
          </Space>
        </div>
        <div className="display-flex-center-h-and-v subscription-plan-card-section pages-info">
          <Typography.Text>
            No. of included pages/{isAnnual ? "year" : "month"}
          </Typography.Text>
        </div>
        <div className="display-flex-center-h-and-v subscription-plan-card-section pages-count">
          <Typography.Text className="subscription-month-text" strong>
            {isAnnual ? numberOfPagesPerYear : numberOfPagesPerMonth}
          </Typography.Text>
        </div>
        <div className="display-flex-center-h-and-v subscription-plan-card-section overage">
          <Typography.Text>Overage</Typography.Text>
        </div>
        <div className="display-flex-center-h-and-v subscription-plan-card-section overage-value">
          <Space className="subscription-price-row" size={4}>
            <Typography.Text className="subscription-month-text" strong>
              ${isAnnual ? overagePerYear : overagePerMonth}
            </Typography.Text>
            <div className="subscription-month-wrapper">
              <Typography.Text className="subscription-overage-month-text">
                /page
              </Typography.Text>
            </div>
          </Space>
        </div>
      </div>
    );
  }
);

SubscriptionPlanCard.displayName = "SubscriptionPlanCard";

SubscriptionPlanCard.propTypes = {
  plan: PropTypes.shape({
    planName: PropTypes.string,
    image: PropTypes.node,
    pricePerYear: PropTypes.string,
    pricePerMonth: PropTypes.string,
    overagePerMonth: PropTypes.string,
    overagePerYear: PropTypes.string,
    numberOfPagesPerMonth: PropTypes.string,
    numberOfPagesPerYear: PropTypes.string,
  }).isRequired,
  isAnnual: PropTypes.bool.isRequired,
  stripePromise: PropTypes.any,
  isAlreadySubscribed: PropTypes.bool.isRequired,
  isSubscriptionCancelled: PropTypes.bool.isRequired,
};

export { SubscriptionPlanCard };
